import { Box, Typography } from '@mui/material'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis
} from 'recharts'

import { formatCurrency } from '../../../../utils'
import { formatAxisCurrency } from './constants'

export interface ChartDatum {
  name: string
  value: number
  color: string
  description?: string
}

export interface LegendItem {
  label: string
  color: string
}

interface SummaryBarChartProps {
  data: ChartDatum[]
  /** 'horizontal' lays bars out sideways — better when there are many metrics with long names. */
  orientation?: 'vertical' | 'horizontal'
  legend?: LegendItem[]
  /** Formats values in labels and tooltips; currency by default. */
  formatValue?: (value: number) => string
  formatAxis?: (value: number) => string
  /** Width reserved for category names in the horizontal layout. */
  categoryWidth?: number
  /** Value labels only appear once the grow-in animation ends, so turn it off where they must show at once. */
  animate?: boolean
}

const SummaryTooltip = ({
  active,
  payload,
  formatValue = formatCurrency
}: TooltipProps<number, string> & { formatValue?: (value: number) => string }) => {
  const datum = payload?.[0]?.payload as ChartDatum | undefined
  if (!active || !datum) return null

  return (
    <Box bgcolor="white" border="1px solid" borderColor="lightGrayThree" borderRadius={1} p={1.5} maxWidth={280}>
      <Typography fontSize={13} fontWeight={600}>
        {datum.name}: {formatValue(datum.value)}
      </Typography>
      {datum.description && (
        <Typography fontSize={12} color="text.secondary" mt={0.5} whiteSpace="pre-line">
          {datum.description}
        </Typography>
      )}
    </Box>
  )
}

/** Single-series bar chart (currency unless told otherwise): one bar per metric, each in its own color, value printed at the bar's end. */
const SummaryBarChart = ({
  data,
  orientation = 'vertical',
  legend,
  formatValue = formatCurrency,
  formatAxis = formatAxisCurrency,
  categoryWidth = 130,
  animate = true
}: SummaryBarChartProps) => {
  const isHorizontal = orientation === 'horizontal'

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        layout={isHorizontal ? 'vertical' : 'horizontal'}
        margin={isHorizontal ? { top: 8, right: 96, left: 8, bottom: 8 } : { top: 24, right: 8, left: 8, bottom: 8 }}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={isHorizontal} horizontal={!isHorizontal} />
        {isHorizontal ? (
          <>
            <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={formatAxis} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={categoryWidth} interval={0} />
          </>
        ) : (
          <>
            <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} />
            <YAxis tick={{ fontSize: 12 }} tickFormatter={formatAxis} />
          </>
        )}
        <Tooltip content={<SummaryTooltip formatValue={formatValue} />} cursor={{ fillOpacity: 0.3 }} />
        {legend && (
          <Legend
            payload={legend.map(item => ({ value: item.label, type: 'square', color: item.color, id: item.label }))}
            formatter={value => <span style={{ color: 'rgba(0, 0, 0, 0.87)' }}>{value}</span>}
          />
        )}
        <Bar
          dataKey="value"
          name="Amount"
          radius={isHorizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}
          maxBarSize={isHorizontal ? 28 : 80}
          isAnimationActive={animate}
        >
          {data.map(datum => (
            <Cell key={datum.name} fill={datum.color} />
          ))}
          <LabelList
            dataKey="value"
            position={isHorizontal ? 'right' : 'top'}
            fontSize={12}
            // Values read in ink, not in the bar's color, so light bars stay legible
            fill="rgba(0, 0, 0, 0.87)"
            formatter={(value: number) => formatValue(value)}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

export default SummaryBarChart
